<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { getColorByLightness } from '$lib/helper';
    import { cn } from '$lib/utils/cn';

    import Color from 'color';
    import { onMount } from 'svelte';

    import messages from './messages.json';

    import type { Language } from '$lib/types/Language';
    import type { Theme } from '$lib/types/Theme';

    export let value: string;
    export let type: 'phone' | 'email';
    export let closeModal: any;
    export let onVerify: (code: string) => void;
    export let onSendAgain: () => void;
    export let isSendAgainDisabled: boolean;
    export let coolDown: number;
    export let theme: Theme;
    export let errorMessage: string | null;
    export let isLoading: boolean;
    export let isVerified: boolean;
    export let language: Language;
    export let previewMessage: string | null = null;

    const btnBgColor = theme.buttonBackgroundColor;
    const btnBgColorDark = Color(btnBgColor).darken(0.1);
    const btnColor = getColorByLightness(btnBgColor);

    let inputRef: HTMLInputElement;
    let otp = '';
    let isOTPFocused = false;
    let isSendingDisabled = true;

    const handleSubmit = (e: SubmitEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const code = formData.get('code') as string;
        onVerify(code);
    };

    const handleOTPChange = (e: Event) => {
        const code = (e.target as HTMLInputElement).value;
        otp = code;

        if (otp.length === 6) {
            isSendingDisabled = false;
            onVerify(otp);
        } else if (otp.length < 6) {
            isSendingDisabled = true;
        }
    };

    const handleOTPFocus = () => {
        isOTPFocused = true;
    };

    const handleOTPBlur = () => {
        isOTPFocused = false;
    };

    onMount(() => {
        inputRef.focus();
    });
</script>

<div
    class="modal fun-relative fun-flex fun-max-w-sm fun-flex-col fun-gap-6 fun-rounded-2xl fun-bg-white fun-p-6"
    data-test-modal-wrap
    role="dialog"
    aria-modal="true"
    aria-labelledby="otp-title"
    tabindex="-1"
>
    {#if !isVerified}
        <button
            aria-label={language === 'de' ? 'Schließen' : 'Close'}
            type="button"
            data-testid="close"
            class="fun-absolute fun-right-4 fun-top-4"
            on:click={() => closeModal()}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="fun-size-4 fun-text-gray-500"
            >
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
        </button>

        <div class="fun-flex fun-flex-col fun-gap-2">
            <h2 class="fun-text-xl fun-font-medium fun-text-gray-900" id="otp-title">
                {type === 'phone' ? messages[language].titlePhone : messages[language].titleEmail}
            </h2>

            <p class="fun-text-sm fun-text-gray-600">
                {type === 'phone'
                    ? messages[language].descriptionPhone
                    : messages[language].descriptionEmail}
                <span class="fun-break-words fun-font-medium fun-text-gray-900">{value}</span>
            </p>
        </div>

        {#if previewMessage}
            <div class="fun-rounded-lg fun-bg-blue-50 fun-p-3 fun-text-sm fun-text-blue-700">
                {previewMessage}
            </div>
        {/if}

        <form on:submit={handleSubmit}>
            <div
                class="fun-relative fun-flex fun-justify-between [&>*]:fun-transition-colors [&>*]:fun-duration-300"
            >
                {#each Array.from({ length: 6 }, (_, i) => i) as index (`otp-input-${index}`)}
                    <div
                        class={cn(
                            'fun-relative fun-flex fun-size-12 fun-items-center fun-justify-center fun-rounded-lg fun-border fun-bg-gray-50',
                            (otp.length === index || (index === 5 && otp.length >= 5)) &&
                                isOTPFocused
                                ? 'fun-border-2 fun-border-gray-900'
                                : 'fun-border-gray-200',
                        )}
                    >
                        <span class="fun-text-xl fun-font-medium fun-text-gray-900"
                            >{otp[index] || ''}</span
                        >
                        {#if otp.length === index && isOTPFocused}
                            <div
                                class="fun-h-4 fun-w-px fun-animate-caret-blink fun-bg-gray-900 fun-duration-1000"
                            />
                        {/if}
                    </div>
                {/each}

                <!-- eslint-disable-next-line svelte/no-unused-svelte-ignore -->
                <!-- svelte-ignore a11y-autofocus -->
                <input
                    type="text"
                    name="code"
                    maxlength="6"
                    pattern="\d*"
                    inputmode="numeric"
                    class="fun-absolute fun-inset-0 fun-bg-transparent fun-text-transparent focus:fun-outline-none"
                    on:input={handleOTPChange}
                    on:focus={handleOTPFocus}
                    on:blur={handleOTPBlur}
                    bind:this={inputRef}
                    disabled={isLoading}
                    aria-label={language === 'de' ? 'Code' : 'Sign-in Code'}
                />
            </div>

            {#if errorMessage}
                <div class="fun-mt-2 fun-text-sm fun-text-red-500">{errorMessage}</div>
            {/if}

            <div class="fun-flex fun-gap-2">
                <button
                    class={cn(
                        'fun-mt-4 fun-w-full fun-transform fun-whitespace-nowrap fun-border fun-border-gray-200 fun-bg-white fun-px-4 fun-py-[10px] fun-text-center fun-text-sm fun-text-gray-900  fun-transition-all hover:fun-bg-gray-100 active:fun-scale-90',
                        getBorderRadius('button'),
                        isSendAgainDisabled
                            ? 'disabled fun-cursor-not-allowed fun-text-gray-400'
                            : '',
                    )}
                    on:click={onSendAgain}
                    disabled={isSendAgainDisabled}
                >
                    {messages[language].buttonSendAgain}
                    {isSendAgainDisabled ? `${coolDown}s` : ''}
                </button>
                <button
                    type="submit"
                    class={cn(
                        'fun-mt-4 fun-flex fun-w-full fun-transform fun-items-center fun-justify-center fun-whitespace-nowrap fun-bg-[var(--buttonBgColor)] fun-px-4 fun-py-[10px] fun-text-center fun-text-sm fun-font-semibold fun-transition-all hover:fun-bg-[var(--buttonBgColorDark)] active:fun-scale-90',
                        getBorderRadius('button'),
                        isSendingDisabled ? 'disabled fun-cursor-not-allowed fun-opacity-50' : '',
                    )}
                    style="--buttonBgColor: {btnBgColor}; color: {btnColor}; --buttonBgColorDark: {btnBgColorDark}"
                    disabled={isSendingDisabled}
                >
                    {#if isLoading}
                        <div
                            class="fun-pointer-events-none fun-flex fun-h-[21px] fun-items-center fun-justify-center"
                        >
                            <span
                                class="fun-block fun-size-4 fun-animate-button-spinner fun-rounded-full fun-border-2 fun-border-gray-200 fun-border-r-transparent fun-border-t-transparent"
                            />
                        </div>
                    {:else}
                        {messages[language].buttonVerify}
                    {/if}
                </button>
            </div>
        </form>
    {:else}
        <div class="fun-flex fun-flex-col fun-items-start fun-gap-6">
            <svg
                width="32"
                height="33"
                viewBox="0 0 32 33"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M15.3222 0.472647C15.7142 0.147138 16.2858 0.147139 16.6778 0.472648C20.5554 3.69236 25.463 5.71446 30.8332 5.97153C31.3159 5.99464 31.7281 6.34329 31.7915 6.82237C31.929 7.86208 32 8.92279 32 10.0001C32 20.3249 25.4802 29.127 16.3331 32.5139C16.1186 32.5933 15.882 32.5933 15.6676 32.5139C6.52009 29.1272 0 20.3249 0 9.99984C0 8.92264 0.0709672 7.86201 0.208481 6.82239C0.271852 6.34329 0.684032 5.99464 1.16675 5.97153C6.537 5.71447 11.4446 3.69237 15.3222 0.472647ZM23.7131 12.3818C24.2004 11.7118 24.0522 10.7737 23.3823 10.2864C22.7123 9.79915 21.7742 9.94728 21.2869 10.6173L14.3194 20.1976L10.5607 16.4389C9.97487 15.8531 9.02513 15.8531 8.43934 16.4389C7.85355 17.0246 7.85355 17.9744 8.43934 18.5602L13.4393 23.5602C13.7495 23.8704 14.18 24.0292 14.6173 23.9949C15.0547 23.9606 15.4551 23.7366 15.7131 23.3818L23.7131 12.3818Z"
                    fill="#111827"
                />
            </svg>

            <div class="fun-flex fun-flex-col fun-items-start fun-gap-2">
                <span class="fun-text-xl fun-font-medium fun-text-gray-900">
                    {messages[language].successTitle}
                </span>
                <span class="fun-text-sm fun-text-gray-600">
                    {type === 'phone'
                        ? messages[language].successDescriptionPhone
                        : messages[language].successDescriptionEmail}
                </span>
            </div>
        </div>
    {/if}
</div>

<style>
    :global(.spm-modal-container) {
        z-index: 1000 !important;
    }
</style>
